import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, Car } from 'lucide-react';
import { PAYMENT_METHODS, PAYMENT_METHOD_ICONS, formatBRL, todayISO } from '../lib/format';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import SignedCurrencyInput from './SignedCurrencyInput';

/**
 * Modal to add or edit a payment.
 * Captures: method, amount, date, optional notes.
 *
 * Layout:
 *   Row 1: forma de pagamento (100% width)
 *   Row 2: data + valor (50% / 50%)
 *   Row 3: observações (textarea, 2 lines)
 */
export default function PaymentDialog({
  open,
  onOpenChange,
  initialMethod = 'PIX',
  initial = null,
  suggestedAmount = 0,
  defaultDate = null,
  onConfirm,
  onRequestTrade,
}) {
  const [form, setForm] = useState({ method: 'PIX', amount: '', notes: '', date: defaultDate || todayISO() });

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          method: initial.method || 'PIX',
          amount: initial.amount ?? '',
          notes: initial.notes || '',
          date: initial.date || defaultDate || todayISO(),
        });
      } else {
        setForm({
          method: initialMethod || 'PIX',
          amount: suggestedAmount > 0 ? suggestedAmount : '',
          notes: '',
          date: defaultDate || todayISO(),
        });
      }
    }
  }, [open, initial, initialMethod, suggestedAmount, defaultDate]);

  const handleConfirm = () => {
    if (form.method === 'TRADE') {
      if (onRequestTrade) {
        onRequestTrade(form.amount ? Number(form.amount) : 0, form.date);
      }
      return;
    }
    // Allow negative values for "troca com troco" scenarios
    if (!form.amount && Number(form.amount) !== 0) {
      toast.error('Informe o valor do pagamento');
      return;
    }
    if (!form.date) {
      toast.error('Informe a data do recebimento');
      return;
    }
    onConfirm({
      method: form.method,
      amount: Number(form.amount),
      notes: form.notes.trim() || null,
      date: form.date,
    });
  };

  const isTrade = form.method === 'TRADE';
  const isFutureDate = form.date && form.date > todayISO();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" /> {initial ? 'Editar pagamento' : 'Adicionar forma de pagamento'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Row 1: Forma de pagamento (100%) */}
          <div>
            <Label>Forma de pagamento *</Label>
            <Select value={form.method} onValueChange={(v) => setForm((f) => ({ ...f, method: v }))}>
              <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PAYMENT_METHODS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {PAYMENT_METHOD_ICONS[k] || ''} {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isTrade ? (
            <>
              <div>
                <Label>Data do recebimento *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  data-testid="payment-date-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Data em que o veículo passou (ou passará) a integrar o estoque.
                </p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm flex items-start gap-2">
                <Car className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-amber-900">Troca selecionada</div>
                  <div className="text-xs text-amber-800 mt-0.5">
                    Ao clicar em "Continuar", será aberto um formulário específico para informar os dados do veículo recebido (placa, marca, modelo, valor avaliado, composição societária).
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Row 2 (Cash): Data + Valor (50/50) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data *</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    data-testid="payment-date-input"
                  />
                </div>
                <div>
                  <Label>Valor *</Label>
                  <SignedCurrencyInput
                    value={form.amount}
                    onChange={(v) => setForm((f) => ({ ...f, amount: v }))}
                    data-testid="payment-amount-modal-input"
                  />
                </div>
              </div>
              {Number(form.amount) < 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm flex items-start gap-2 -mt-2">
                  <span className="text-lg">💰</span>
                  <div className="flex-1">
                    <div className="font-medium text-amber-900">Troca com troco</div>
                    <div className="text-xs text-amber-800 mt-0.5">
                      Valor negativo indica que a empresa <strong>devolverá dinheiro ao comprador</strong> (quando o veículo dado na troca vale mais que o veículo vendido).
                    </div>
                  </div>
                </div>
              )}
              {isFutureDate && (
                <p className="text-[11px] text-amber-700 -mt-2 font-medium">
                  📅 Data futura — entrará no caixa como previsão de recebimento.
                </p>
              )}
              {suggestedAmount > 0 && !initial && (
                <p className="text-[11px] text-muted-foreground -mt-2">
                  Sugestão de valor: {formatBRL(suggestedAmount)} (saldo restante)
                </p>
              )}
              {/* Row 3: Observações (textarea 2 linhas) */}
              <div>
                <Label>Observações</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Ex: Banco Santander, parcelado em 36x..."
                  rows={2}
                  data-testid="payment-notes-input"
                  className="resize-none"
                />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleConfirm}
            className={isTrade ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90'}
            data-testid="payment-confirm-button"
          >
            {isTrade ? 'Continuar para detalhes da troca →' : (initial ? 'Atualizar' : 'Adicionar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
